import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Mail,
  Phone,
  Home,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  Copy,
  Send,
  ArrowLeft,
  Search,
  Filter,
  Key,
  Shield,
  FileText,
  MoreVertical,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResidentManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('residents');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Invite dialog
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    resident_name: '',
    unit_number: '',
    email: '',
    mobile_number: '',
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Action dialog
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', invitation: null });
  
  const getToken = useCallback(() => {
    return localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  }, []);

  const fetchInvitations = useCallback(async () => {
    try {
      const token = getToken();
      const buildingId = user?.hoa_id || 'demo_hoa';
      
      const response = await fetch(`${API_URL}/api/invitations/building/${buildingId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInvitations(data);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  }, [user, getToken]);

  const fetchAuditLog = useCallback(async () => {
    try {
      const token = getToken();
      const buildingId = user?.hoa_id || 'demo_hoa';
      
      const response = await fetch(`${API_URL}/api/invitations/audit-log/${buildingId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuditLog(data);
      }
    } catch (error) {
      console.error('Error fetching audit log:', error);
    }
  }, [user, getToken]);

  useEffect(() => {
    fetchInvitations();
    fetchAuditLog();
  }, [fetchInvitations, fetchAuditLog]);

  const handleInviteResident = async () => {
    if (!inviteForm.resident_name || !inviteForm.unit_number || !inviteForm.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = getToken();
      const buildingId = user?.hoa_id || 'demo_hoa';
      
      const response = await fetch(`${API_URL}/api/invitations/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...inviteForm,
          building_id: buildingId
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Invitation sent! Code: ${data.access_code}`);
        setShowInviteDialog(false);
        setInviteForm({ resident_name: '', unit_number: '', email: '', mobile_number: '' });
        fetchInvitations();
        fetchAuditLog();
      } else {
        toast.error(data.detail || 'Failed to send invitation');
      }
    } catch (error) {
      toast.error('Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/invitations/${invitationId}/resend`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Invitation resent! New code: ${data.new_code}`);
        fetchInvitations();
        fetchAuditLog();
      } else {
        toast.error(data.detail || 'Failed to resend invitation');
      }
    } catch (error) {
      toast.error('Failed to resend invitation');
    }
    setActionDialog({ open: false, type: '', invitation: null });
  };

  const handleResetCode = async (invitationId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/invitations/${invitationId}/reset-code`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Code reset! New code: ${data.new_code}`);
        fetchInvitations();
        fetchAuditLog();
      } else {
        toast.error(data.detail || 'Failed to reset code');
      }
    } catch (error) {
      toast.error('Failed to reset code');
    }
    setActionDialog({ open: false, type: '', invitation: null });
  };

  const handleDeactivate = async (invitationId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/invitations/${invitationId}/deactivate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Resident deactivated successfully');
        fetchInvitations();
        fetchAuditLog();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to deactivate');
      }
    } catch (error) {
      toast.error('Failed to deactivate resident');
    }
    setActionDialog({ open: false, type: '', invitation: null });
  };

  const handleRemove = async (invitationId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Invitation removed successfully');
        fetchInvitations();
        fetchAuditLog();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to remove');
      }
    } catch (error) {
      toast.error('Failed to remove invitation');
    }
    setActionDialog({ open: false, type: '', invitation: null });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'activated':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge>;
      case 'expired':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200"><XCircle className="w-3 h-3 mr-1" />Expired</Badge>;
      case 'deactivated':
        return <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200"><AlertCircle className="w-3 h-3 mr-1" />Deactivated</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredInvitations = invitations.filter(inv => {
    const matchesSearch = inv.resident_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         inv.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         inv.unit_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: invitations.length,
    active: invitations.filter(i => i.status === 'activated').length,
    pending: invitations.filter(i => i.status === 'pending').length,
    expired: invitations.filter(i => i.status === 'expired').length,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Residents Management</h1>
                <p className="text-sm text-slate-500">Invite and manage building residents</p>
              </div>
            </div>
            <Button onClick={() => setShowInviteDialog(true)} className="gap-2" data-testid="invite-resident-btn">
              <UserPlus className="w-4 h-4" />
              Invite Resident
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                  <p className="text-xs text-slate-500">Total Invitations</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.active}</p>
                  <p className="text-xs text-slate-500">Active Residents</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.pending}</p>
                  <p className="text-xs text-slate-500">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.expired}</p>
                  <p className="text-xs text-slate-500">Expired</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="residents" className="gap-2">
              <Users className="w-4 h-4" />
              Residents
            </TabsTrigger>
            <TabsTrigger value="audit" className="gap-2">
              <FileText className="w-4 h-4" />
              Audit Log
            </TabsTrigger>
          </TabsList>

          <TabsContent value="residents">
            {/* Filters */}
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by name, email, or unit..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={statusFilter === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setStatusFilter('all')}
                    >
                      All
                    </Button>
                    <Button
                      variant={statusFilter === 'activated' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setStatusFilter('activated')}
                    >
                      Active
                    </Button>
                    <Button
                      variant={statusFilter === 'pending' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setStatusFilter('pending')}
                    >
                      Pending
                    </Button>
                    <Button
                      variant={statusFilter === 'expired' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setStatusFilter('expired')}
                    >
                      Expired
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Invitations List */}
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-2" />
                <p className="text-slate-500">Loading...</p>
              </div>
            ) : filteredInvitations.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No residents found</h3>
                  <p className="text-slate-500 mb-4">Start by inviting residents to your building</p>
                  <Button onClick={() => setShowInviteDialog(true)}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invite Resident
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredInvitations.map((inv) => (
                  <Card key={inv.invitation_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="p-3 bg-slate-100 rounded-full">
                            <Users className="w-6 h-6 text-slate-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900">{inv.resident_name}</h3>
                              {getStatusBadge(inv.status)}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-500">
                              <div className="flex items-center gap-1">
                                <Home className="w-4 h-4" />
                                Unit {inv.unit_number}
                              </div>
                              <div className="flex items-center gap-1">
                                <Mail className="w-4 h-4" />
                                {inv.email}
                              </div>
                              {inv.mobile_number && (
                                <div className="flex items-center gap-1">
                                  <Phone className="w-4 h-4" />
                                  {inv.mobile_number}
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {new Date(inv.created_at).toLocaleDateString()}
                              </div>
                            </div>
                            {inv.status === 'pending' && (
                              <div className="mt-2 flex items-center gap-2">
                                <Badge variant="secondary" className="font-mono">
                                  <Key className="w-3 h-3 mr-1" />
                                  {inv.access_code}
                                </Badge>
                                <Button variant="ghost" size="sm" onClick={() => copyCode(inv.access_code)}>
                                  <Copy className="w-3 h-3" />
                                </Button>
                                <span className="text-xs text-slate-400">
                                  Expires: {new Date(inv.expires_at).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          {inv.status === 'pending' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setActionDialog({ open: true, type: 'resend', invitation: inv })}
                              >
                                <Send className="w-3 h-3 mr-1" />
                                Resend
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setActionDialog({ open: true, type: 'reset', invitation: inv })}
                              >
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Reset Code
                              </Button>
                            </>
                          )}
                          {inv.status === 'activated' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-orange-600 hover:text-orange-700"
                              onClick={() => setActionDialog({ open: true, type: 'deactivate', invitation: inv })}
                            >
                              <XCircle className="w-3 h-3 mr-1" />
                              Deactivate
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => setActionDialog({ open: true, type: 'remove', invitation: inv })}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Remove
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="audit">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Activity Log
                </CardTitle>
              </CardHeader>
              <CardContent>
                {auditLog.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No activity recorded yet</p>
                ) : (
                  <div className="space-y-3">
                    {auditLog.map((entry, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className={`p-2 rounded-full ${
                          entry.action === 'invitation_created' ? 'bg-blue-100' :
                          entry.action === 'account_activated' ? 'bg-green-100' :
                          entry.action === 'resident_deactivated' ? 'bg-red-100' :
                          'bg-slate-100'
                        }`}>
                          {entry.action === 'invitation_created' ? <UserPlus className="w-4 h-4 text-blue-600" /> :
                           entry.action === 'account_activated' ? <CheckCircle2 className="w-4 h-4 text-green-600" /> :
                           entry.action === 'resident_deactivated' ? <XCircle className="w-4 h-4 text-red-600" /> :
                           <RefreshCw className="w-4 h-4 text-slate-600" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">
                            {entry.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          <p className="text-sm text-slate-600">{entry.details}</p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                            <span>{entry.resident_name} • Unit {entry.unit_number}</span>
                            <span>By: {entry.performed_by_name}</span>
                            <span>{new Date(entry.timestamp).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Invite Resident
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="resident_name">Resident Name *</Label>
              <Input
                id="resident_name"
                placeholder="John Smith"
                value={inviteForm.resident_name}
                onChange={(e) => setInviteForm({ ...inviteForm, resident_name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="unit_number">Unit Number *</Label>
              <Input
                id="unit_number"
                placeholder="e.g., 101, A1, 12B"
                value={inviteForm.unit_number}
                onChange={(e) => setInviteForm({ ...inviteForm, unit_number: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="mobile">Mobile Number (Optional)</Label>
              <Input
                id="mobile"
                placeholder="+27 82 123 4567"
                value={inviteForm.mobile_number}
                onChange={(e) => setInviteForm({ ...inviteForm, mobile_number: e.target.value })}
              />
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> A unique access code will be generated and sent to the resident's email.
                The code expires after 7 days.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
            <Button onClick={handleInviteResident} disabled={submitting}>
              {submitting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, type: '', invitation: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {actionDialog.type === 'resend' && 'Resend Invitation'}
              {actionDialog.type === 'reset' && 'Reset Access Code'}
              {actionDialog.type === 'deactivate' && 'Deactivate Resident'}
              {actionDialog.type === 'remove' && 'Remove Invitation'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {actionDialog.type === 'resend' && (
              <p>This will generate a new access code and send it to <strong>{actionDialog.invitation?.email}</strong>.</p>
            )}
            {actionDialog.type === 'reset' && (
              <p>This will generate a new access code for <strong>{actionDialog.invitation?.resident_name}</strong>. The old code will no longer work.</p>
            )}
            {actionDialog.type === 'deactivate' && (
              <p className="text-orange-600">This will deactivate <strong>{actionDialog.invitation?.resident_name}</strong>'s access to the building. They will no longer be able to log in.</p>
            )}
            {actionDialog.type === 'remove' && (
              <p className="text-red-600">This will permanently remove the invitation for <strong>{actionDialog.invitation?.resident_name}</strong>. This action cannot be undone.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, type: '', invitation: null })}>
              Cancel
            </Button>
            <Button
              variant={actionDialog.type === 'remove' || actionDialog.type === 'deactivate' ? 'destructive' : 'default'}
              onClick={() => {
                if (actionDialog.type === 'resend') handleResendInvitation(actionDialog.invitation.invitation_id);
                if (actionDialog.type === 'reset') handleResetCode(actionDialog.invitation.invitation_id);
                if (actionDialog.type === 'deactivate') handleDeactivate(actionDialog.invitation.invitation_id);
                if (actionDialog.type === 'remove') handleRemove(actionDialog.invitation.invitation_id);
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
